'use client';

import { useParams } from 'next/navigation';
import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { SendMessage } from '@/app/[lang]/(web)/contact-us/components/ContactUsForm';
import Input from '@/components/core/Input';
import Toaster from '@/components/popups/Toaster';
import { useDeviceSize } from '@/hooks/useDeviceSize';
import { useTranslation } from '@/hooks/useTranslations';
import { useVisiblePathname } from '@/hooks/useVisiblePathname';
import './styles.scss';

const JoinOurTeam: FC = () => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({ email: '' });
  const [success, setSuccess] = useState(false);

  const { isMobile } = useDeviceSize();
  const params = useParams();
  const pathname = useVisiblePathname();
  const { t } = useTranslation();

  const showBecomePartnerRoutes = [
    { mobile: true, desktop: true, route: `/${params.lang}` },
    {
      mobile: false,
      desktop: true,
      route: `/${params.lang}/contact-us`,
    },
  ];

  const shouldShow = showBecomePartnerRoutes.some(
    (route) =>
      route.route === pathname && ((isMobile && route.mobile) || (!isMobile && route.desktop))
  );

  useEffect(() => {
    let timer: any;
    if (success) {
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [success]);

  if (!shouldShow) {
    return null;
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setErrors({ email: '' });
    setEmail(event.target.value);
  };

  const handleValidate = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      setErrors({ email: 'Invalid email' });
      return true;
    }
    return false;
  };

  const onSubmit = async () => {
    if (handleValidate()) {
      return;
    }
    try {
      return await SendMessage({ email }, 'subscribe').then(() => {
        setEmail('');
        setSuccess(true);
        setErrors({ email: '' });
        toast(<Toaster type="success" description="Subscription-successful-Text" />, {
          duration: 3000,
        });
      });
    } catch (error) {
      setErrors({ email: 'Failed to send message' });
    }
  };

  return (
    <div className="joinNewsletterHolder">
      <div className="newsletterGroupData">
        <h4 className="newsletterTitleText">
          {success ? t('Congratulations-Text') : t('Contactus-Newsletter-Title')}
        </h4>
        <div className="newsletterDescContainer">
          <p className="newsletterDescText">
            {success
              ? 'You have been successfully subscribed to our newsletter!'
              : t('Contactus-Newsletter-Paragraph')}
          </p>
          {success && (
            <div className="success-icon">
              <i className="bc-i-check" />
            </div>
          )}
        </div>
        {!success && (
          <div className="newsletterSubscribe">
            <div className="popOkInputBlock">
              <Input
                name="email"
                value={email}
                autoComplete="off"
                placeholder={t('Homepage-Form-Email')}
                onBlur={handleValidate}
                onChange={handleChange}
                className="popOkInput hasBorder"
              />
              {errors.email && (
                <p className="error-message white-error">Please fill email correctly</p>
              )}
            </div>
            <div className="newsletterBtnWrp">
              <button
                onClick={onSubmit}
                className="button btnTypeBig btnTypeOrange"
                id="newsletter-subscribe"
              >
                <p>{t('Contactus-Newsletter-Subscribe')}</p>
                <i className="bc-i-go-to btnIcon btnIconSmall" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinOurTeam;
